
import useIsEmea from "@dana/Common/Hooks/useIsEmea";
import waitFor from "@insite/client-framework/Common/Utilities/waitFor";
import {
    makeHandlerChainAwaitable,
    replaceInChain
} from "@insite/client-framework/HandlerCreator";
import { getShouldLoadInventory, getShouldLoadPricing } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getBrandStateById } from "@insite/client-framework/Store/Data/Brands/BrandsSelectors";
import loadBrand from "@insite/client-framework/Store/Data/Brands/Handlers/LoadBrand";
import { getCategoryState } from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import {chain,PrepareStructuredPageData} from "@insite/client-framework/Store/Pages/ProductDetails/Handlers/GenerateStructuredPageData";

if (module.hot) {
    module.hot.accept();
    const originalChain = chain.slice();
    module.hot.dispose(data => {
        chain.length = 0;
        originalChain.forEach(o => chain.push(o));
    });
} 

const ensureDomain = (url: string,websiteUrl:string): string => {
    const urlWebiste = new URL(websiteUrl);
  url = url.trim();
  if (/^https?:\/\//i.test(url)) return url;
  if (url.includes(".") && !url.startsWith("/")) return "https://" + url;
  return urlWebiste.origin + "/" + url.replace(/^\/+/, "");
};

replaceInChain(
    chain,
    PrepareStructuredPageData,
    async props => {
        const {
                id,
                productTitle,
                smallImagePath,
                mediumImagePath,
                largeImagePath,
                canonicalUrl,
                manufacturerItem,
                detail,
                brand: productBrand,
                content,
                productNumber,
                attributeTypes,            
            } = props.parameter.product;
            const state = props.getState();
           const isEmea = useIsEmea(state);
            if (content === undefined) {
                /**
                 * sometimes content may not be loaded because product was initially loaded for product carousel
                 * in this case we should not set structured data and wait until product with all data is loaded
                 */
                return false;
            }
            
        const upcAttribute = attributeTypes?.find((a) => a.name?.toLowerCase() === "upc code");
        const upcValue = upcAttribute?.attributeValues?.[0]?.value;
        const productName =(productBrand?.name ? productBrand.name + " " : "") + productTitle;
        const gtinKey = isEmea ? "gtin13" : "gtin12";
            const data = {
                "@context": "https://schema.org",
                "@type": "Product",                
                name: productName,
                image: [largeImagePath],
                mpn: productNumber,
                sku: productNumber,
                [gtinKey]:upcValue,
                url: ensureDomain(canonicalUrl,state.context.website.uri),                
            } as any;
        
            if (content?.htmlContent) {
                data.description = content.htmlContent.replace(/<[^>]+>/g, "").replace(/[\r\n\t]/g, "");
            }
        
            let productInfo = props.parameter.productInfo;
            if (
                productInfo &&
                !props.parameter.product.quoteRequired &&
                getShouldLoadPricing(state) &&
                getShouldLoadInventory(state)
            ) {
                await waitFor(() => {
                    const productInfosById = props.getState().pages.productDetails.productInfosById;
                    if (productInfo && productInfosById && productInfosById[productInfo.productId]) {
                        productInfo = productInfosById[productInfo.productId];
                    }
        
                    return !!productInfo!.pricing && !!productInfo!.inventory;
                });
        
                const { pricing, inventory, unitOfMeasure } = productInfo;
        
                if (pricing && inventory && pricing.unitNetPrice > 0) {
                    const availability = inventory?.inventoryAvailabilityDtos?.find(
                        o => o.unitOfMeasure.toLowerCase() === (unitOfMeasure?.toLowerCase() || ""),
                    )?.availability;
        
                    data.offers = {
                        "@type": "Offer",
                        availability:
                            availability?.messageType !== 2
                                ? availability?.messageType === 3
                                    ? "https://schema.org/LimitedAvailability"
                                    : "https://schema.org/InStock"
                                : "https://schema.org/OutOfStock",
                        price: pricing.unitNetPrice,
                        priceCurrency: props.parameter.session?.currency?.currencyCode,
                        url: ensureDomain(canonicalUrl,state.context.website.uri),
                    };
                }
            }
        
            if (props.parameter.brandId) {
                let brandState = getBrandStateById(state, props.parameter.brandId);
                let brand = productBrand || brandState.value;
                if (!brand && !brandState.isLoading) {
                    const awaitableLoadBrand = makeHandlerChainAwaitable(loadBrand);
                    await awaitableLoadBrand({ brandId: props.parameter.brandId })(props.dispatch, props.getState);
                    brandState = getBrandStateById(props.getState(), props.parameter.brandId);
                    brand = brandState.value;
                }
        
                if (brand) {
                    data.brand = {
                        "@type": "Brand",                       
                        name: brand.name,                       
                    };
                }
                
            }
            data.manufacturer ={
                        "@type": "Organization",       
                        name:state.context.website.companyName,
                        url:state.context.website.companyUrl,       
                    }
            if (props.parameter.categoryIdWithBrandId || props.parameter.categoryId) {
                const category = getCategoryState(
                    state,
                    props.parameter.categoryIdWithBrandId || props.parameter.categoryId || "",
                ).value;
        
                if (category) {
                    data.category = category.path;
                }
            }
        
            props.result = { structuredPageData: JSON.stringify(data) };
        },
);